import { useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll, useSpring } from "framer-motion";
import { MapPin, Users, SlidersHorizontal, Mail, MessageCircle } from "lucide-react";
import { STORY_STEPS } from "@/data/content";

const ICONS = [MapPin, Users, SlidersHorizontal, Mail, MessageCircle];
const EASE = [0.22, 1, 0.36, 1];

function Step({ step, index, on }) {
  const Icon = ICONS[index];
  return (
    <motion.li className="relative pb-16 pl-20 last:pb-0 sm:pl-24" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-15%" }} transition={{ duration: 0.9, ease: EASE }} data-testid={`story-step-${step.n}`}>
      <div className={`glass absolute left-0 top-0 grid h-14 w-14 place-items-center !rounded-full transition-all duration-700 ${on ? "!border-[#96C11F]/60 shadow-[0_0_40px_rgba(150,193,31,0.35)]" : ""}`}>
        <Icon className={`h-5 w-5 transition-colors duration-700 ${on ? "text-[#C6EE5A]" : "text-white/40"}`} />
      </div>
      <h3 className={`text-2xl font-bold tracking-[-0.03em] transition-colors duration-700 sm:text-[2rem] ${on ? "text-white" : "text-white/55"}`}>{step.title}</h3>
      <p className="mt-3 max-w-md text-[15px] leading-relaxed text-white/60">{step.body}</p>
    </motion.li>
  );
}

export default function MarketStory() {
  const listRef = useRef(null);
  const [active, setActive] = useState(-1);
  const { scrollYProgress } = useScroll({ target: listRef, offset: ["start 0.7", "end 0.55"] });
  const line = useSpring(scrollYProgress, { stiffness: 110, damping: 28 });
  useMotionValueEvent(scrollYProgress, "change", (v) => setActive(v <= 0.01 ? -1 : Math.min(4, Math.floor(v * 4.6))));

  return (
    <section id="how" className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 lg:py-40" data-testid="market-story-section">
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="self-start lg:sticky lg:top-32 lg:col-span-5">
          <p className="eyebrow">Why now</p>
          <h2 className="mt-6 text-4xl font-extrabold leading-[1.02] tracking-[-0.045em] sm:text-5xl lg:text-[3.75rem]">A market hiding in plain sight.</h2>
          <p className="mt-8 text-base leading-relaxed text-white/70 sm:text-[17px]">
            Agents spend heavily competing for the same homeowners at the same moment — once they have requested a valuation, called a rival or appeared on the portals.
          </p>
          <p className="mt-5 text-base leading-relaxed text-white/70 sm:text-[17px]">
            This is about reaching relevant, established homeowners earlier: the households whose life stage and property suggest a next move may be on the horizon.
          </p>
          <div className="glass mt-10 flex items-start gap-4 p-6">
            <span className="mt-1 h-10 w-1 rounded-full bg-gradient-to-b from-[#C6EE5A] to-[#7AA818]" />
            <p className="text-lg font-semibold leading-snug tracking-[-0.02em] text-white">
              Reach homeowners before the valuation.<span className="text-white/50"> Find the next move before it reaches the portals.</span>
            </p>
          </div>
        </div>

        <div className="relative lg:col-span-7 lg:pt-6">
          <div className="absolute bottom-6 left-[27px] top-6 w-px bg-white/10" aria-hidden="true">
            <motion.div className="h-full w-full origin-top bg-gradient-to-b from-[#C6EE5A] via-[#96C11F] to-[#96C11F]/0" style={{ scaleY: line }} />
          </div>
          <ol ref={listRef} className="relative">
            {STORY_STEPS.map((s, i) => (
              <Step key={s.n} step={s} index={i} on={i <= active} />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
